import { useScreenSize } from '@composables/useScreenSize';
import { useAuthStore } from '@/stores/auth.store';
import { useCookiesStore } from '@/stores/cookies.store';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';

// Create a singleton instance
let gaInstance = null;

const HANDOFF_REFERRER_KEY = 'gcGa4HandoffReferrer';

// Cross-domain handoff params (gc_tid / gc_cid / gc_sid / gc_sct) captured
// ONCE at module load. Two reasons this must happen this early:
//   1. initializeAnalytics may not run until the user accepts cookie consent;
//      by then Vue Router may have replaced the query string and the handoff
//      would be silently lost.
//   2. If the guest shares or bookmarks the booking-engine URL with these
//      params still on it, every subsequent visitor would inherit the first
//      visitor's client_id / session_id. We strip them from the URL via
//      history.replaceState as soon as they're captured.
// The apikey query param is literally 'gc' — filter by exact key name, never
// by prefix, or the site apikey gets wiped along with the handoff keys.
const CAPTURED_HANDOFF = (() => {
  if (typeof window === 'undefined' || !window.location) return null;
  let url;
  try {
    url = new URL(window.location.href);
  } catch (e) {
    return null;
  }
  const params = url.searchParams;
  const captured = {
    gcTid: params.get('gc_tid'),
    gcCid: params.get('gc_cid'),
    gcSid: params.get('gc_sid'),
    gcSct: params.get('gc_sct'),
    // document.referrer at module load is the URL of the page that opened
    // this tab (hotel site) — Vue Router navigations do not change it, so
    // this survives late analytics init after consent. Used to sanity-check
    // gc_tid on GTM-only hotels where hotel_ga4_code is empty; a crafted
    // link from an attacker's own origin never matches.
    refererHostname: null,
  };
  if (!captured.gcTid && !captured.gcCid && !captured.gcSid) return null;

  try {
    if (typeof document !== 'undefined' && document.referrer) {
      captured.refererHostname = new URL(
        document.referrer,
      ).hostname.toLowerCase();
    }
  } catch (e) {
    // Malformed referrer — treat as absent.
  }

  // Mobile channel-key reload is same-origin, so document.referrer becomes
  // this engine host. Restore the hotel hostname stashed before that href.
  try {
    const here = window.location.hostname.toLowerCase();
    if (!captured.refererHostname || captured.refererHostname === here) {
      const stashed = sessionStorage.getItem(HANDOFF_REFERRER_KEY);
      if (stashed) captured.refererHostname = stashed.toLowerCase();
    }
    sessionStorage.removeItem(HANDOFF_REFERRER_KEY);
  } catch (e) {
    // sessionStorage blocked — GTM-hotel ownership may fail on this load.
  }

  try {
    let changed = false;
    for (const key of ['gc_tid', 'gc_cid', 'gc_sid', 'gc_sct']) {
      if (params.has(key)) {
        params.delete(key);
        changed = true;
      }
    }
    if (changed && typeof window.history?.replaceState === 'function') {
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch (e) {
    // History API unavailable — the handoff still applies to this pageview;
    // only the URL-persistence protection is skipped.
  }

  return captured;
})();

/**
 * Re-attach captured gc_* to a URLSearchParams used in a full reload
 * (mobile website→mobile channel). Also stashes the hotel referrer hostname:
 * after location.href, document.referrer is this engine origin and GTM-only
 * ownership would otherwise drop gc_tid.
 *
 * @param {URLSearchParams} urlParams
 * @returns {void}
 */
export const applyCapturedHandoffToSearchParams = (urlParams) => {
  if (!CAPTURED_HANDOFF || !urlParams) return;
  if (CAPTURED_HANDOFF.gcTid) urlParams.set('gc_tid', CAPTURED_HANDOFF.gcTid);
  if (CAPTURED_HANDOFF.gcCid) urlParams.set('gc_cid', CAPTURED_HANDOFF.gcCid);
  if (CAPTURED_HANDOFF.gcSid) urlParams.set('gc_sid', CAPTURED_HANDOFF.gcSid);
  if (CAPTURED_HANDOFF.gcSct) urlParams.set('gc_sct', CAPTURED_HANDOFF.gcSct);
  try {
    if (CAPTURED_HANDOFF.refererHostname) {
      sessionStorage.setItem(
        HANDOFF_REFERRER_KEY,
        CAPTURED_HANDOFF.refererHostname,
      );
    }
  } catch (e) {
    // sessionStorage blocked — second load still has gc_*; direct-GA4 hotels
    // validate via hotel_ga4_code and do not need the referrer.
  }
};

// Case-insensitive hostname match after stripping a leading "www." from both
// sides — either variant can appear in document.referrer depending on the
// hotel's canonical URL vs the one the guest actually browsed.
const normaliseHostname = (host) =>
  typeof host === 'string' ? host.toLowerCase().replace(/^www\./, '') : '';

/**
 * Seed the BE-domain GA4 cookies with the hotel site's session so gtag/js
 * and the hotel GTM tag join that session instead of minting a new one.
 * gtag('config', { session_id }) is not enough: cookie state on THIS host
 * is what GA4 reads. Must run after gc_tid ownership is validated and
 * before gtag.js / gtm.js load.
 *
 * Always overwrite both cookies on a validated handoff — the engine host
 * is shared across hotels, so a leftover _ga from a previous visit would
 * keep the wrong client_id. Cookie max-age matches Google (2 years);
 * the 30-minute session window lives inside the GS2 value, not the expiry.
 */
const seedGa4Cookies = (gcTid, gcCid, gcSid, gcSct) => {
  if (typeof document === 'undefined' || !gcTid || !gcCid || !gcSid) return;
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const maxAge = '; path=/; max-age=63072000; SameSite=Lax' + secure;
    document.cookie = '_ga=GA1.1.' + gcCid + maxAge;

    const now = Math.floor(Date.now() / 1000);
    const suffix = String(gcTid).replace(/^G-/i, '').toUpperCase();
    const sct = /^\d+$/.test(gcSct || '') ? gcSct : '1';
    // GS2: $s session_id, $o session_number (sct), $g engaged, $t last hit.
    const cookieVal = 'GS2.1.s' + gcSid + '$o' + sct + '$g1$t' + now;
    document.cookie = '_ga_' + suffix + '=' + cookieVal + maxAge;
  } catch (e) {
    // document.cookie blocked — gtag config still carries client_id / session_id.
  }
};

/**
 * Hook for Google Analytics and Google Tag Manager integration
 * @returns {Object} Object containing analytics methods and state
 */
export const useGoogleAnalytics = () => {
  if (gaInstance) {
    return gaInstance;
  }

  const cookiesStore = useCookiesStore();
  const { userCookies } = storeToRefs(cookiesStore);

  /**
   * Initializes Google Analytics and Google Tag Manager
   * @param {Object} gaConfig - Configuration object for analytics
   * @param {string} gaConfig.mbg_ga_code - Google Analytics tracking ID for mobile
   * @param {string} gaConfig.bg_ga_code - Google Analytics tracking ID for desktop
   * @param {string} gaConfig.hotel_ga4_code - Hotel's own GA4 Measurement ID, empty when it tracks through Tag Manager
   * @param {string} gaConfig.gtm_container_id - Google Tag Manager container ID
   * @param {Array} gaConfig.external_gtm - Array of external GTM container IDs
   * @returns {void}
   */
  const initializeAnalytics = (gaConfig) => {
    if (!gaConfig) {
      console.warn('Google Analytics configuration is required');
      return;
    }

    /*if (!userCookies.value.analyticsCookies) {
      // Do not initialize if analytics cookies are not accepted
      return;
    }*/

    const { isMobile } = useScreenSize();
    const trackingId = isMobile.value
      ? gaConfig.mbg_ga_code
      : gaConfig.bg_ga_code;
    const gtmContainerId = gaConfig.gtm_container_id;
    const externalGtm = gaConfig.external_gtm || [];

    // Cross-domain handoff from the hotel site (DS Pro): apply hotel G- config
    // BEFORE GTM loads so the first hit reuses identity instead of starting a
    // fresh session on this domain. Values come from CAPTURED_HANDOFF (module
    // top) — captured once at boot and already stripped from the URL. Anything
    // malformed is dropped rather than forwarded to gtag: a crafted link must
    // not be able to pin every visitor to the same client_id or session.
    const readCapturedHandoff = (name, pattern) => {
      const value = CAPTURED_HANDOFF ? CAPTURED_HANDOFF[name] : null;
      return value && pattern.test(value) ? value : null;
    };
    const gcCid = readCapturedHandoff('gcCid', /^\d+\.\d+$/);
    const gcSid = readCapturedHandoff('gcSid', /^\d+$/);
    // Two paths accept a gc_tid:
    //   1. hotel_ga4_code is set (direct-GA4 hotel) and gc_tid matches it —
    //      the strongest signal, exact ownership match.
    //   2. hotel_ga4_code is empty (GTM-only hotel, where the real G- lives
    //      inside the container and the backend cannot see it), AND the
    //      document.referrer hostname matches one of hotel_domains. The
    //      browser sets the referrer; a crafted link from an attacker's own
    //      origin cannot forge it. Referrer stripped or hostname absent →
    //      drop gc_tid (bookings still complete without a handoff).
    const gcTidRaw = readCapturedHandoff('gcTid', /^G-[A-Z0-9]+$/i);
    const gcTidCandidate = gcTidRaw ? gcTidRaw.toUpperCase() : null;
    const hotelGa4Code =
      typeof gaConfig.hotel_ga4_code === 'string' &&
      /^G-[A-Z0-9]+$/i.test(gaConfig.hotel_ga4_code)
        ? gaConfig.hotel_ga4_code.toUpperCase()
        : null;
    const refererHost = normaliseHostname(
      CAPTURED_HANDOFF ? CAPTURED_HANDOFF.refererHostname : null,
    );
    const hotelDomainSet = new Set(
      (Array.isArray(gaConfig.hotel_domains) ? gaConfig.hotel_domains : [])
        .map(normaliseHostname)
        .filter(Boolean),
    );
    const refererMatchesHotel =
      refererHost !== '' && hotelDomainSet.has(refererHost);
    const gcTid = (() => {
      if (!gcTidCandidate) return null;
      if (hotelGa4Code) {
        return gcTidCandidate === hotelGa4Code ? gcTidCandidate : null;
      }
      return refererMatchesHotel ? gcTidCandidate : null;
    })();
    const gcSct = readCapturedHandoff('gcSct', /^\d+$/);
    const sidNum = gcSid ? Number(gcSid) : NaN;
    const hasSid = Number.isFinite(sidNum);
    const gtmWillLoad = Boolean(
      gtmContainerId || (externalGtm && externalGtm.length),
    );

    // Cookie is the source of truth for session on this host. Seed before
    // any gtag/gtm script so both join the site's session.
    if (gcTid && gcCid && gcSid) {
      seedGa4Cookies(gcTid, gcCid, gcSid, gcSct);
    }

    const buildGaConfig = (withHandoff) => {
      const cfg = {
        linker: { accept_incoming: true },
      };
      if (withHandoff) {
        if (gcCid) cfg.client_id = gcCid;
        if (hasSid) cfg.session_id = sidNum;
        // Hotel referrer would otherwise count as a campaign change and
        // force a new session (Ads origin lost even with the same sid).
        cfg.ignore_referrer = true;
        // Always send this page_view. Hotel GTM on the engine often does
        // not fire a GA4 page_view for the hotel G- (session would start
        // on a later scroll / event instead of the landing). Cookies +
        // ignore_referrer keep GTM from opening a second session if it
        // also sends one.
      }
      return cfg;
    };

    const injectGtm = () => {
      if (!gtmWillLoad) return;

      const allGtmContainerIds = [
        ...(gtmContainerId ? [gtmContainerId] : []),
        ...(externalGtm || []),
      ];

      const addGtmScript = (containerId) => {
        if (document.getElementById(`gtm-script-${containerId}`)) return;
        const gtmScript = document.createElement('script');
        gtmScript.id = `gtm-script-${containerId}`;
        gtmScript.async = true;
        gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}&t=${Date.now()}`;
        document.head.appendChild(gtmScript);
      };

      const addGtmNoscript = (containerId) => {
        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        noscript.appendChild(iframe);
        document.body.insertBefore(noscript, document.body.firstChild);
      };

      allGtmContainerIds.forEach(addGtmScript);

      if (userCookies.value.analyticsCookies) {
        allGtmContainerIds.forEach(addGtmNoscript);
      }
    };

    // Queue gtag/js, then load GTM only after it has processed config so
    // the hotel container does not mint a session before identity is set.
    // Fail-open: gtag onerror / 2s timeout still injects GTM.
    const loadGtagThenGtm = (id) => {
      const startGtm = (() => {
        let once = false;
        return () => {
          if (once) return;
          once = true;
          injectGtm();
        };
      })();

      if (!id) {
        startGtm();
        return;
      }
      if (document.getElementById('ga-script')) {
        startGtm();
        return;
      }

      const gaScript = document.createElement('script');
      gaScript.id = 'ga-script';
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
      gaScript.onload = startGtm;
      gaScript.onerror = startGtm;
      document.head.appendChild(gaScript);
      setTimeout(startGtm, 2000);
    };

    // dataLayer + GTM bootstrap first (same as before for GTM-only hotels)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const hasHandoffTarget = Boolean(gcTid && gcCid);
    const needsGtag = Boolean(trackingId) || hasHandoffTarget;

    if (needsGtag) {
      // Queue gtag commands before gtag/js arrives so handoff client_id is
      // applied on the first hit (and baseline UA/G- still configs as before).
      window.gtag =
        window.gtag ||
        function () {
          window.dataLayer.push(arguments);
        };
      window.gtag('js', new Date());

      const configuredIds = new Set();
      const configAndLoad = (id, withHandoff) => {
        if (!id) return;
        const key = String(id).toUpperCase();
        if (configuredIds.has(key)) return;
        configuredIds.add(key);
        window.gtag('config', id, buildGaConfig(withHandoff));
      };

      // 1) Hotel Measurement ID from the site — config with handoff ASAP
      if (hasHandoffTarget) {
        configAndLoad(gcTid, true);
      }

      // 2) Existing BE tracker (often GC UA-/G-). Attach handoff only when a
      // resolved gcTid matches this tracker — never on gcCid alone. The BE
      // domain is shared across hotels, so applying an unvetted client_id to
      // whatever G- the BE happens to track would let a crafted ?gc_cid=X
      // link pin identity on the shared property.
      if (trackingId) {
        const trackingIdNorm = String(trackingId).toUpperCase();
        const isGa4TrackingId = trackingIdNorm.indexOf('G-') === 0;
        const applyHandoffToTrackingId =
          Boolean(gcCid) &&
          Boolean(gcTid) &&
          isGa4TrackingId &&
          trackingIdNorm === gcTid;
        configAndLoad(trackingId, applyHandoffToTrackingId);
      }

      window.gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        analytics_storage: 'granted',
      });

      loadGtagThenGtm(gcTid || trackingId);
    } else {
      injectGtm();
    }
  };

  // Get auth store for channelKey
  const authStore = useAuthStore();

  /**
   * Ensures dataLayer exists in the window object
   * @returns {Array} The dataLayer array
   */
  const ensureDataLayer = () => {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  };

  /**
   * Tracks a page view in Google Analytics
   * @param {string} path - The page path to track
   * @param {string} [hotelId] - Optional hotel ID to prepend to the path
   * @returns {void}
   */
  const trackPageView = (path, hotelId) => {
    if (!window.gtag || !userCookies.value.analyticsCookies) return;
    window.gtag('event', 'page_view', {
      page_path: hotelId ? `${hotelId}${path}` : path,
    });
  };

  const trackBeginCheckoutEventEcommerce = (data) => {
    if (!userCookies.value.analyticsCookies) {
      console.warn('Analytics cookies not accepted');
      return;
    }

    const items = [];
    //iterate through each data object
    data.forEach((res) => {
      const itemId = res.room_rate.room_rate_code;
      const itemName = res.room.name + ' - ' + res.rate_plan.name;
      const itemCategory = 'room';
      const itemPrice = res.room_rate.total_price;
      const itemQuantity = 1;
      items.push({
        item_id: itemId,
        item_name: itemName,
        item_category: itemCategory,
        price: itemPrice,
        quantity: itemQuantity,
      });

      const beginCheckout = {
        event: 'begin_checkout',
        currency: res.room_rate.currency,
        value: res.room_rate.total_price,
        items: items,
      };

      pushToDataLayer(beginCheckout);
    });
  };

  const trackAddPaymentInfoEventEcommerce = (data) => {
    if (!userCookies.value.analyticsCookies) {
      console.warn('Analytics cookies not accepted');
      return;
    }

    const items = [];
    //iterate through each data object
    data.forEach((res) => {
      const itemId = res.room_rate.room_rate_code;
      const itemName = res.room.name + ' - ' + res.rate_plan.name;
      const itemCategory = 'room';
      const itemPrice = res.room_rate.total_price;
      const itemQuantity = 1;
      items.push({
        item_id: itemId,
        item_name: itemName,
        item_category: itemCategory,
        price: itemPrice,
        quantity: itemQuantity,
      });

      const beginCheckout = {
        event: 'add_payment_info',
        currency: res.room_rate.currency,
        value: res.room_rate.total_price,
        items: items,
      };

      pushToDataLayer(beginCheckout);
    });
  };

  /**
   * Tracks a booking event with both standard GTM format and enhanced ecommerce
   * @param {Array<Object>} reservations - Array of reservation objects
   * @returns {void}
   */
  const trackBookedEventEcommerce = (reservations) => {
    // Check if analytics cookies are accepted
    if (!userCookies.value.analyticsCookies) {
      console.warn('Analytics cookies not accepted');
      return;
    }

    const route = useRoute();

    // Also track with the original format for backward compatibility
    // Iterate through each reservation and push to dataLayer
    reservations.forEach((res) => {
      // Calculate number of nights
      const checkIn = new Date(res.checkin);
      const checkOut = new Date(res.checkout);
      const nights = Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24));

      //Create a new array of objects for addons
      const addons = res.addons
        ? res.addons.map((addon) => ({
            name: addon.name,
            netPriceTotal: addon.net_price,
          }))
        : [];

      //create reservation object
      const gcReservation = {
        code: res.hotel_reservation_code,
        propertyName: res.hotel.hotel_name,
        offer: res.room_type.name + ' - ' + res.rate_plan.name,
        offerId: res.room_rate.room_rate_code,
        mealPlan: res.meal_plan.name,
        checkIn: res.checkin,
        checkOut: res.checkout,
        nights: nights,
        quantity: 1,
        adults: res.number_of_adults,
        children: res.number_of_children,
        addons: res.addons || [],
      };

      //create price object
      const gcPrice = {
        currency: res.room_rate.currency,
        total: res.room_rate.total_price,
        net: res.room_rate.net_price,
        netAddons:
          addons && addons.length > 0
            ? addons.reduce((sum, addon) => sum + addon.netPriceTotal, 0)
            : 0,
        netStay: res.room_rate.net_price,
        taxes: res.room_rate.taxes,
        fees: res.room_rate.fees,
        perUnit: res.room_rate.net_price,
      };

      //create guest object
      const gcGuest = {
        firstName: res.guest.first_name,
        lastName: res.guest.last_name,
        name: `${res.guest.first_name} ${res.guest.last_name}`,
        city: res.guest.city,
        zip: res.guest.postal_code,
        country: res.guest.country,
        payment: res.credit_card ? res.credit_card.name || '' : '',
      };

      const transProducts = res.addons
        ? res.addons.map((addon) => ({
            item_id: addon.name,
            item_name: addon.name,
            item_category: 'addon',
            price: parseFloat(addon.net_price),
            quantity: addon.quantity,
          }))
        : [];

      transProducts.push({
        item_id: gcReservation.offerId,
        item_name: gcReservation.offer,
        item_category: 'room',
        price: parseFloat(gcPrice.perUnit),
        quantity: gcReservation.quantity,
      });

      //create ecommerce object
      const gcEcommerce = {
        event: 'purchase',
        transaction_id: res.hotel_reservation_code,
        affiliation: authStore.channelKey ? authStore.channelKey : '',
        value: res.room_rate.total_price,
        currency: res.room_rate.currency,
        tax: res.room_rate.taxes,
        items: transProducts,
        utm_source: route.query.utm_source || '',
        utm_medium: route.query.utm_medium || '',
        utm_campaign: route.query.utm_campaign || '',
        utm_content: route.query.utm_content || '',
      };

      const eventData = {
        event: 'booked',
        // Reservation details
        gcReservation: gcReservation,
        //price details
        gcPrice: gcPrice,
        // Guest details
        gcGuest: gcGuest,
        // Metadata fields
        gcReferer: res.booking_referer,
        gcLanguage: res.guest.language,
        gcCustomParam: res.custom_param || '',
        gcMobile: useScreenSize().isMobile ? true : false,
        gcClientId: res.hotel.hotel_id,
        gcChannelKey: authStore.channelKey ? authStore.channelKey : '',
        // ecommerce
        transactionId: res.hotel_reservation_code,
        transactionAffiliation: authStore.channelKey
          ? authStore.channelKey
          : '',
        transactionTotal: res.room_rate.total_price,
        transactionTax: res.room_rate.taxes,
        transactionShipping: '-',
        transactionProducts: transProducts,
        ecommerce: gcEcommerce,
      };

      // Push to dataLayer
      pushToDataLayer(eventData);
      pushToDataLayer(gcEcommerce);
    });
  };

  /**
   * Pushes data to the Google Tag Manager dataLayer
   * @param {Object} data - Data object to push to the dataLayer
   * @returns {void}
   */
  const pushToDataLayer = (data) => {
    if (!userCookies.value.analyticsCookies) {
      console.warn('Analytics cookies not accepted');
      return;
    }

    const dataLayer = ensureDataLayer();
    dataLayer.push(data);
  };

  /**
   * Pushes a standard event to the Google Tag Manager dataLayer
   * @param {string} eventName - Name of the event
   * @param {Object} [eventData={}] - Additional event data
   * @returns {void}
   */
  const pushEvent = (eventName, eventData = {}) => {
    pushToDataLayer({
      event: eventName,
      ...eventData,
    });
  };

  /**
   * Clears previous ecommerce object from dataLayer to prevent data leakage
   * @returns {void}
   */
  const clearEcommerceData = () => {
    pushToDataLayer({
      ecommerce: null,
    });
  };

  /**
   * Tracks an event in both Google Analytics and Google Tag Manager
   * @param {string} eventName - Name of the event
   * @param {Object} [eventParams={}] - Additional event parameters
   * @returns {void}
   */
  const trackEvent = (eventName, eventParams = {}) => {
    // Track with GA if analytics cookies are accepted
    if (window.gtag && userCookies.value.analyticsCookies) {
      window.gtag('event', eventName, eventParams);
    }

    // Also track with GTM if analytics cookies are accepted
    if (userCookies.value.analyticsCookies) {
      pushEvent(eventName, eventParams);
    }
  };

  /**
   * Removes Google Analytics and Tag Manager scripts from the page
   * @returns {void}
   */
  const stopAnalytics = () => {
    if (userCookies.value.analyticsCookies) {
      // Do not initialize if analytics cookies are not accepted
      return;
    }
    // Remove all GA/GTM related scripts and iframes
    const ids = [
      'GTM-',
      'UA-',
      'G-',
      'gtm.js',
      'gtag/js',
      'analytics.js',
      'gtag/js',
    ];

    // Remove all scripts and iframes
    document.querySelectorAll('script, iframe').forEach((el) => {
      if (
        ids.some((id) => el.src?.includes(id)) ||
        el.id?.startsWith('gtm-script-') ||
        el.id === 'ga-script' ||
        el.innerText?.includes('gtag') ||
        el.innerText?.includes('dataLayer') ||
        el.innerText?.includes('analytics')
      ) {
        el.remove();
      }
    });

    // Remove all GTM noscript iframes (for all GTM containers)
    const gtmNoscripts = document.querySelectorAll(
      'noscript iframe[src*="googletagmanager.com/ns.html"]',
    );
    gtmNoscripts.forEach((noscript) => {
      noscript.parentElement?.remove();
    });

    // Clear all analytics-related global variables
    const analyticsGlobals = [
      'gtag',
      'google_tag_manager',
      '_ga',
      '_gat',
      '_gid',
      'ga',
      'gaData',
      'gaGlobal',
      'GoogleAnalyticsObject',
      'dataLayer',
      'google_analytics_uacct',
      'google_optimize',
      'google_tag_data',
      'google_tag_manager',
      'google_analytics_domain_name',
      'google_analytics_uacct',
      'google_analytics_uacct_id',
      'google_analytics_uacct_type',
      'google_analytics_uacct_version',
    ];

    analyticsGlobals.forEach((globalVar) => {
      delete window[globalVar];
    });

    // Override dataLayer to prevent any new data from being pushed

    // Clear any existing cookies
    const cookiePrefixes = [
      '_ga',
      '_gid',
      '_gat',
      '_gac_',
      '_fbp',
      '_fb',
      'AMP_TOKEN',
      '_gcl_',
    ];
    const cookieNames = [
      ...cookiePrefixes,
      ...Array.from({ length: 26 }, (_, i) =>
        Array.from(
          { length: 26 },
          (_, j) =>
            `_gcl_${String.fromCharCode(97 + i)}${String.fromCharCode(97 + j)}`,
        ),
      ).flat(),
    ];

    cookieNames.forEach((cookieName) => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
    });

    // Clear localStorage and sessionStorage
    Object.keys(localStorage).forEach((key) => {
      if (
        key.includes('_ga') ||
        key.includes('_gcl') ||
        key.includes('gtm') ||
        key.includes('analytics')
      ) {
        localStorage.removeItem(key);
      }
    });

    Object.keys(sessionStorage).forEach((key) => {
      if (
        key.includes('_ga') ||
        key.includes('_gcl') ||
        key.includes('gtm') ||
        key.includes('analytics')
      ) {
        sessionStorage.removeItem(key);
      }
    });
  };

  gaInstance = {
    initializeAnalytics,
    trackPageView,
    trackEvent,
    trackBookedEventEcommerce,
    trackBeginCheckoutEventEcommerce,
    trackAddPaymentInfoEventEcommerce,
    pushToDataLayer,
    pushEvent,
    clearEcommerceData,
    stopAnalytics,
  };

  return gaInstance;
};
